import Excel from 'exceljs';
import FileSaver from 'file-saver';
import {
    addTotalsRow,
    autoSizeColumns,
    boldRow,
    EXCEL_DATE_TIME_FORMAT,
    EXCEL_NUMBER_FORMAT,
    styleDateColumns,
    styleNumberColumns,
    toExcelCalendarDate,
    toExcelInstant,
} from '../../../../helpers/excel-utils';
import { sumBy } from '../../../../helpers';
import {
    AccountingWorkSheets,
    ExpenseRow,
    formatTransferList,
    LedgerRow,
    MISSING_REQUIREMENT_LABEL,
    MissingRequirement,
    OrderSaleRow,
    PendingCounts,
    pendingCounts,
    PendingDocument,
} from './accounting-export-rules';

// A canceled document must not keep a transfer attached; until that is enforced
// at capture time the export flags the leftovers instead of hiding them.
const CANCELED_DOCUMENT_ALERT = '⚠ DOCUMENTO CANCELADO';
const LINKED_TRANSFER_ALERT = '⚠ TRANSFERENCIA LIGADA';
const ALERT_FONT: Partial<Excel.Font> = {
    bold: true,
    color: { argb: 'FFC00000' },
};
const PENDING_FONT: Partial<Excel.Font> = {
    bold: true,
    color: { argb: 'FFB8860B' },
};

const MISSING_REQUIREMENT_ORDER: MissingRequirement[] = [
    'FOLIO',
    'SUPPLEMENT',
    'CREDIT_NOTE',
    'TAX',
];

function ledgerPendingAlert(row: LedgerRow): string {
    const missing: string[] = [];
    if (row.missingFolio) missing.push('Falta factura');
    if (row.missingSupplement) missing.push('Complemento');
    if (row.missingCreditNote) missing.push('Nota de crédito');
    if (row.missingTax) missing.push('IVA');
    return missing.length > 0 ? `⚠ ${missing.join(', ')}` : '';
}

function orderSalePendingAlert(row: OrderSaleRow): string {
    const missing: string[] = [];
    if (row.missingInvoiceCode) missing.push('Falta factura');
    if (row.missingSupplement) missing.push('Complemento');
    if (row.missingCreditNote) missing.push('Nota de crédito');
    if (row.missingTax) missing.push('IVA');
    return missing.length > 0 ? `⚠ ${missing.join(', ')}` : '';
}

function expensePendingAlert(row: ExpenseRow): string {
    const missing: string[] = [];
    if (row.missingExternalCode) missing.push('Falta factura');
    if (row.missingSupplement) missing.push('Complemento');
    if (row.missingTax) missing.push('IVA');
    return missing.length > 0 ? `⚠ ${missing.join(', ')}` : '';
}

/**
 * Writes the month's workbook and hands it to the browser.
 *
 * `worksheets` is already the export set (see `buildAccountingExport`): this
 * builder applies no eligibility rule of its own, so the workbook cannot contain
 * a row the screen did not mark, or drop one it did not.
 */
export async function downloadAccountingWorkbook({
    worksheets,
    monthLabel,
    pendingBankSales,
    pendingBankExpenses,
    pendingSales,
    pendingExpenses,
}: {
    worksheets: AccountingWorkSheets;
    monthLabel: string;
    pendingBankSales: PendingDocument[];
    pendingBankExpenses: PendingDocument[];
    pendingSales: PendingDocument[];
    pendingExpenses: PendingDocument[];
}): Promise<void> {
    const workbook = new Excel.Workbook();
    const {
        accountsPages,
        orderSalePage,
        canceledOrderSalePage,
        expensePage,
        canceledExpensePage,
    } = worksheets;

    const exportedLedgerRows = accountsPages.reduce<LedgerRow[]>(
        (acc, page) => acc.concat(page.data),
        [],
    );

    // ── Resumen ──────────────────────────────────────────────────────────────
    // Built from the same rows the sheets below receive, so the orientation page
    // can never disagree with the detail it summarizes.
    const ivaVentas = sumBy(orderSalePage, (s) => s.tax);
    const ivaGastos = sumBy(expensePage, (e) => e.tax);
    const ivaRetenido = sumBy(expensePage, (e) => e.taxRetained);
    const isrRetenido = sumBy(expensePage, (e) => e.nonTaxRetained);

    const resumenWs = workbook.addWorksheet('Resumen');
    // Amounts and counts share column B but need different formats, so each row
    // states its own.
    const addResumenRow = (
        label: string,
        value: string | number | Date,
        note: string,
        numFmt?: string,
    ) => {
        const row = resumenWs.addRow([label, value, note]);
        if (numFmt) row.getCell(2).numFmt = numFmt;
        return row;
    };

    boldRow(resumenWs.addRow([`Contabilidad ${monthLabel}`]));
    addResumenRow(
        'Generado',
        toExcelInstant(new Date().toISOString()) ?? '',
        'Fecha y hora de descarga de este archivo',
        EXCEL_DATE_TIME_FORMAT,
    );
    resumenWs.addRow([]);

    boldRow(resumenWs.addRow(['IVA', 'Valor', 'Nota']));
    addResumenRow(
        'IVA Ventas',
        ivaVentas,
        'Suma del IVA de las facturas de clientes no canceladas',
        EXCEL_NUMBER_FORMAT,
    );
    addResumenRow(
        'IVA Gastos',
        ivaGastos,
        'Suma del IVA de las facturas de proveedores no canceladas',
        EXCEL_NUMBER_FORMAT,
    );
    addResumenRow(
        'IVA menos retenciones',
        ivaGastos - ivaRetenido - isrRetenido,
        'IVA Gastos - IVA retenido - ISR retenido',
        EXCEL_NUMBER_FORMAT,
    );
    addResumenRow(
        'Diferencia',
        ivaVentas - ivaGastos,
        'IVA Ventas - IVA Gastos',
        EXCEL_NUMBER_FORMAT,
    );
    resumenWs.addRow([]);

    boldRow(resumenWs.addRow(['Transferencias por cuenta', 'Valor', 'Nota']));
    accountsPages.forEach((accountPage) => {
        addResumenRow(
            accountPage.accountName,
            sumBy(accountPage.data, (row) => row.transferredAmount),
            '',
            EXCEL_NUMBER_FORMAT,
        );
    });
    addTotalsRow(resumenWs, [
        'Total transferencias',
        sumBy(exportedLedgerRows, (row) => row.transferredAmount),
        'Incluye traspasos entre cuentas propias, como el estado de cuenta',
    ]).getCell(2).numFmt = EXCEL_NUMBER_FORMAT;
    resumenWs.addRow([]);

    boldRow(resumenWs.addRow(['Documentos', 'Cantidad', 'Nota']));
    (
        [
            ['Facturas clientes', orderSalePage.length],
            ['Facturas canceladas clientes', canceledOrderSalePage.length],
            ['Facturas proveedores', expensePage.length],
            ['Facturas canceladas proveedores', canceledExpensePage.length],
        ] as [string, number][]
    ).forEach(([label, count]) => {
        addResumenRow(label, count, '', '0');
    });
    resumenWs.addRow([]);

    // ── Pendientes ───────────────────────────────────────────────────────────
    // The same counts the page shows above the tabs.
    boldRow(resumenWs.addRow(['Documentos pendientes', 'Cantidad', 'Nota']));
    resumenWs.addRow([
        '',
        '',
        'Documentos incluidos en el export con requisitos pendientes. Los cancelados no se cuentan.',
    ]);
    const addPendingRows = (label: string, counts: PendingCounts) => {
        addResumenRow(
            `${label}: documentos con faltantes`,
            counts.documents,
            'Un documento con dos faltantes cuenta una sola vez en este renglón',
            '0',
        );
        MISSING_REQUIREMENT_ORDER.forEach((requirement) => {
            if (counts.byRequirement[requirement] === 0) return;
            addResumenRow(
                `${label}: ${MISSING_REQUIREMENT_LABEL[requirement]}`,
                counts.byRequirement[requirement],
                '',
                '0',
            );
        });
    };
    addPendingRows('Bancos · Ventas', pendingCounts(pendingBankSales));
    addPendingRows('Bancos · Gastos', pendingCounts(pendingBankExpenses));
    addPendingRows('Ventas', pendingCounts(pendingSales));
    addPendingRows('Gastos', pendingCounts(pendingExpenses));

    resumenWs.getColumn(2).alignment = { horizontal: 'right' };
    autoSizeColumns(resumenWs);
    // The note column carries full sentences; cap it so the sheet still opens at
    // a readable width.
    resumenWs.getColumn(3).width = Math.min(
        resumenWs.getColumn(3).width ?? 60,
        60,
    );

    // ── Bank-account summaries ───────────────────────────────────────────────
    accountsPages.forEach((accountPage) => {
        const { data: accountPageData, accountName } = accountPage;
        const ws = workbook.addWorksheet(accountName);
        boldRow(
            ws.addRow([
                '#',
                'Tipo',
                'De',
                'Para',
                'Dia/hora de la transferencia',
                'Folio de la factura',
                'Fecha de emision de la factura',
                'Cantidad transferida',
                'Cantidad de la factura',
                'IVA de la factura',
                'Iva retenido',
                'Isr Retenido',
                'Notas',
                'Complemento',
                'Nota de credito',
                'Alerta',
            ]),
        );
        accountPageData.forEach((data) => {
            const pending = ledgerPendingAlert(data);
            const alertText = data.documentCanceled
                ? CANCELED_DOCUMENT_ALERT
                : pending;
            const row = ws.addRow([
                data.transferIndex,
                data.transferType,
                data.fromAccountName,
                data.toAccountName,
                toExcelInstant(data.transferDate),
                data.orderCode,
                toExcelCalendarDate(data.receiptIssueDate),
                data.transferredAmount,
                data.receiptTotal,
                data.receiptTax,
                data.taxRetained,
                data.nonTaxRetained,
                data.notes,
                data.supplement,
                data.creditNote,
                alertText,
            ]);
            if (data.documentCanceled) {
                row.getCell(16).font = ALERT_FONT;
            } else if (pending) {
                row.getCell(16).font = PENDING_FONT;
            }
        });
        addTotalsRow(ws, [
            'Total',
            null,
            null,
            null,
            null,
            null,
            null,
            sumBy(accountPageData, (r) => r.transferredAmount),
            sumBy(accountPageData, (r) => r.receiptTotal),
            sumBy(accountPageData, (r) => r.receiptTax),
            sumBy(accountPageData, (r) => r.taxRetained),
            sumBy(accountPageData, (r) => r.nonTaxRetained),
            null,
            null,
            null,
            null,
        ]);
        styleDateColumns(ws, [5], EXCEL_DATE_TIME_FORMAT);
        styleDateColumns(ws, [7]);
        styleNumberColumns(ws, [8, 9, 10, 11, 12]);
        autoSizeColumns(ws);
    });

    // ── Invoice registers ────────────────────────────────────────────────────
    const orderSaleColumns = [
        'Folio',
        'Fecha de emisión',
        'Cliente',
        'Total (con descuentos e IVA)',
        'IVA',
        'Cantidad transferida',
        'Transferencias',
        'Complemento',
        'Nota de credito',
        'Notas',
    ];

    const addOrderSaleSheet = (
        title: string,
        rows: OrderSaleRow[],
        alertOnLinkedTransfer = false,
    ) => {
        const ws = workbook.addWorksheet(title);
        const columns = orderSaleColumns.concat('Alerta');
        const alertColumn = columns.length;
        boldRow(ws.addRow(columns));
        rows.forEach((orderSale) => {
            const pending = orderSalePendingAlert(orderSale);
            const alertText = alertOnLinkedTransfer && orderSale.transfers.length > 0
                ? LINKED_TRANSFER_ALERT
                : pending;
            const values: (string | number | Date | null)[] = [
                orderSale.invoiceCode,
                toExcelCalendarDate(orderSale.date),
                orderSale.accountName,
                orderSale.total,
                orderSale.tax,
                orderSale.transferReceiptsTotal,
                formatTransferList(orderSale.transfers),
                orderSale.supplement,
                orderSale.creditNote,
                orderSale.notes,
                alertText,
            ];
            const row = ws.addRow(values);
            if (alertOnLinkedTransfer && orderSale.transfers.length > 0) {
                row.getCell(alertColumn).font = ALERT_FONT;
            } else if (pending) {
                row.getCell(alertColumn).font = PENDING_FONT;
            }
        });
        addTotalsRow(ws, [
            'Total',
            null,
            null,
            sumBy(rows, (r) => r.total),
            sumBy(rows, (r) => r.tax),
            sumBy(rows, (r) => r.transferReceiptsTotal),
            null,
            null,
            null,
            null,
            null,
        ]);
        styleDateColumns(ws, [2]);
        styleNumberColumns(ws, [4, 5, 6]);
        autoSizeColumns(ws);
    };

    addOrderSaleSheet(`Facturas clientes ${monthLabel}`, orderSalePage);
    addOrderSaleSheet(
        `Facturas canceladas clientes ${monthLabel}`,
        canceledOrderSalePage,
        true,
    );

    const expenseColumns = [
        'Folio',
        'Fecha de emisión',
        'Proveedor',
        'IVA',
        'IVA retenido',
        'ISR retenido',
        'Total con IVA',
        'Cantidad transferida',
        'Transferencias',
        'Complemento',
        'Notas',
    ];

    const addExpenseSheet = (
        title: string,
        rows: ExpenseRow[],
        alertOnLinkedTransfer = false,
    ) => {
        const ws = workbook.addWorksheet(title);
        const columns = expenseColumns.concat('Alerta');
        const alertColumn = columns.length;
        boldRow(ws.addRow(columns));
        rows.forEach((expense) => {
            const pending = expensePendingAlert(expense);
            const alertText = alertOnLinkedTransfer && expense.transfers.length > 0
                ? LINKED_TRANSFER_ALERT
                : pending;
            const values: (string | number | Date | null)[] = [
                expense.orderCode,
                toExcelCalendarDate(expense.date),
                expense.accountName,
                expense.tax,
                expense.taxRetained,
                expense.nonTaxRetained,
                expense.totalWithTax,
                expense.transferReceiptsTotal,
                formatTransferList(expense.transfers),
                expense.supplement,
                expense.notes,
                alertText,
            ];
            const row = ws.addRow(values);
            if (alertOnLinkedTransfer && expense.transfers.length > 0) {
                row.getCell(alertColumn).font = ALERT_FONT;
            } else if (pending) {
                row.getCell(alertColumn).font = PENDING_FONT;
            }
        });
        addTotalsRow(ws, [
            'Total',
            null,
            null,
            sumBy(rows, (r) => r.tax),
            sumBy(rows, (r) => r.taxRetained),
            sumBy(rows, (r) => r.nonTaxRetained),
            sumBy(rows, (r) => r.totalWithTax),
            sumBy(rows, (r) => r.transferReceiptsTotal),
            null,
            null,
            null,
            null,
        ]);
        styleDateColumns(ws, [2]);
        styleNumberColumns(ws, [4, 5, 6, 7, 8]);
        autoSizeColumns(ws);
    };

    addExpenseSheet(`Facturas proveedores ${monthLabel}`, expensePage);
    addExpenseSheet(
        `Facturas canceladas proveedores ${monthLabel}`,
        canceledExpensePage,
        true,
    );

    const buffer = await workbook.xlsx.writeBuffer();
    FileSaver.saveAs(new Blob([buffer]), `contabilidad-${monthLabel}.xlsx`);
}
